import React, { useEffect, useState } from "react";
import classes from "./inputField.module.css";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/style.css';
import CalendarIcon from '../../../assets/images/calendar.png';
import Calendar from '../calendar/calendar'; // Import your CalendarRange component

const InputField = ({
  elementType,
  elementConfig,
  value,
  changed,
  invalid,
  shouldValidate,
  touched,
  label,
  withRemove,
  withIcon,
  isMultiple,
  style,
  disabled,
  ...otherProps
}) => {
  let inputElement = null;
  const [showPassword, setShowPassword] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentLang, setCurrentLang] = useState(document.documentElement.lang || 'en');
  const [phoneInput, setPhoneInput] = useState({ dialCode: otherProps.defaultPhoneCode || '', number: '' });
  const [placeholderText, setPlaceholderText] = useState('');
  
  const buttonClasses = [classes.ButtonElement];
  const dropdownClasses = [classes.DropdownElement];
  const inputClasses = [classes.InputElement];
  
  // Apply RTL class based on current language
  if (currentLang === 'ar') { // Arabic
    inputClasses.push(classes.RTL);
  }
  
  // Check for language changes
  useEffect(() => {
    const checkLanguage = () => {
      const htmlLang = document.documentElement.lang;
      if (htmlLang !== currentLang) {
        setCurrentLang(htmlLang);
        // Update placeholder immediately when language changes
        updatePlaceholder(htmlLang);
      }
    };
    
    // Check immediately and set up an interval
    checkLanguage();
    const intervalId = setInterval(checkLanguage, 300);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [currentLang, elementConfig.placeholder]);
  
  // Initial placeholder setup
  useEffect(() => {
    updatePlaceholder(currentLang);
  }, [elementConfig.placeholder]);
  
  // Helper to update placeholder based on language
  const updatePlaceholder = (lang) => {
    const placeholder = getPlaceholder(lang);
    setPlaceholderText(placeholder);
  };
  
  let showValidation = false;
  let validationMessage = "";
  if ((invalid && shouldValidate && touched) || !!otherProps.validationMessage) {
    inputClasses.push(classes.Invalid);
    showValidation = true;
  }
  if (shouldValidate?.required)
    validationMessage = otherProps.validationMessage ?? (
      <>
        {label} {currentLang === 'en' ? "is required" : "مطلوب"}
      </>
    );
  if (shouldValidate?.required && shouldValidate?.minLength === 6 && shouldValidate?.maxLength === 32)
    validationMessage = otherProps.validationMessage ?? (
      <>
        {label} {currentLang === 'en' 
          ? "is required and must be greater than 6 characters and less than 32 characters" 
          : "مطلوب ويجب أن يكون أكثر من 6 أحرف وأقل من 32 حرفًا"}
      </>
    );
  if (shouldValidate?.required && shouldValidate?.minLength === 2 && shouldValidate?.maxLength === 32)
    validationMessage = otherProps.validationMessage ?? (
      <>
        {label} {currentLang === 'en' 
          ? "is required and must be greater than 2 characters and less than 32 characters" 
          : "مطلوب ويجب أن يكون أكثر من 2 أحرف وأقل من 32 حرفًا"}
      </>
    );
  if (shouldValidate?.required && shouldValidate?.minLength === 10)
    validationMessage = otherProps.validationMessage ?? (
      <>
        {label} {currentLang === 'en' 
          ? "is required and must be 10 characters" 
          : "مطلوب ويجب أن يكون 10 أحرف"}
      </>
    );

  const setChecked = item => {
    return item === value;
  };
  
  useEffect(() => {
    if (!phoneInput.number && otherProps.defaultPhoneCode) {
      setPhoneInput({ dialCode: otherProps.defaultPhoneCode, number: '' });
    }
  }, [otherProps.defaultPhoneCode]);

  useEffect(() => {
    if (value) {
      setPhoneInput({ dialCode: value.dialCode || '', number: value.number || '' });
    }
  }, [value]);

  const handlePhoneChange = (value, countryData) => {
    const phoneData = {
      dialCode: countryData.dialCode,
      number: value.slice(countryData.dialCode.length),
    };
    setPhoneInput(phoneData);
    changed(phoneData);
  };
  
  //handle date input click
  const handleDateInputClick = () => {
    setShowCalendar(!showCalendar);
  };
  
  const handleDateSelect = (date) => {
    setSelectedDates(date);
    changed(date);
    setShowCalendar(false);
  };

  const getInputProps = () => {
    const baseProps = {
      className: inputClasses.join(" "),
      ...elementConfig,
      value,
      onChange: changed,
      style,
      disabled,
      placeholder: placeholderText,
      dir: currentLang === 'ar' ? "rtl" : "ltr",
    };

    if (isMultiple) {
      baseProps.multiple = true;
    }

    return baseProps;
  };

  // Helper to get placeholder text based on current language
  const getPlaceholder = (lang) => {
    if (otherProps.placeholder) {
      // Handle direct props placeholder
      if (typeof otherProps.placeholder === 'string' && otherProps.placeholder.includes('|')) {
        const [enText, arText] = otherProps.placeholder.split('|');
        return lang === 'en' ? enText.trim() : arText.trim();
      }
      return otherProps.placeholder;
    }
    
    if (elementConfig.placeholder) {
      // If the placeholder contains both English and Arabic text separated by '|'
      if (typeof elementConfig.placeholder === 'string' && elementConfig.placeholder.includes('|')) {
        const [enText, arText] = elementConfig.placeholder.split('|');
        return lang === 'en' ? enText.trim() : arText.trim();
      }
      return elementConfig.placeholder;
    }
    
    return lang === 'en' ? "Enter value" : "أدخل قيمة";
  };

  // Get phone placeholder text
  const getPhonePlaceholder = () => {
    if (elementConfig.placeholder && typeof elementConfig.placeholder === 'string') {
      if (elementConfig.placeholder.includes('|')) {
        const [enText, arText] = elementConfig.placeholder.split('|');
        return currentLang === 'en' ? enText.trim() : arText.trim();
      }
      return elementConfig.placeholder;
    }
    return currentLang === 'en' ? "Enter phone number" : "أدخل رقم الهاتف";
  };

  switch (elementType) {
    case "input": {
      switch (elementConfig.type) {
        case "password":
          inputElement = (
            <input
              {...getInputProps()}
              type={showPassword ? "text" : "password"}
              minLength={8}
            />
          );
          break;
        case "phone":
          inputElement = (
            <PhoneInput
              inputClass={inputClasses.join(" ")}
              inputStyle={{ 
                direction: "ltr", 
                width: "100%", 
                height: 48,
                textAlign: "left"
              }}
              buttonClass={buttonClasses.join(" ")}
              buttonStyle={{ 
                background: "var(--Secondary-Tint-7, #96ADC2)", 
                borderRight: "none",
                borderLeft: "1px solid #ccc",
                width: "62px"
              }}
              dropdownClass={dropdownClasses.join(" ")}
              dropdownStyle={{ direction: 'ltr' }}
              value={phoneInput.dialCode + phoneInput.number}
              onChange={handlePhoneChange}
              disabled={disabled}
              country={'jo'} // Default country (Jordan for +962)
              placeholder="07xxxxxxxx"
              enableSearch={true}
              searchPlaceholder="search"
              containerClass=""
              inputProps={{
                name: 'phone',
                required: true,
                dir: "ltr"
              }}
              containerStyle={{
                direction: "ltr"
              }}
              searchStyle={{
                direction: "ltr",
                textAlign: "left"
              }}
            />
          );
          break;
        case "calendarDate":
          inputElement = (
            <input
              {...getInputProps()}
              onClick={handleDateInputClick}
            />
          );
          break;
        default:
          inputElement = (
            <input
              {...getInputProps()}
              onClick={otherProps.clicked}
            />
          );
      }
      break;
    }
    case "textarea":
      inputElement = (
        <textarea
          style={style}
          className={inputClasses.join(" ")}
          {...elementConfig}
          value={value}
          onChange={changed}
          placeholder={placeholderText}
          dir={currentLang === 'ar' ? "rtl" : "ltr"}
        />
      );
      break;
    case "select":
      inputElement = (
        <select
          style={style}
          className={inputClasses.join(" ")}
          value={value ? value : otherProps.defaultId}
          onChange={changed}
          dir={currentLang === 'ar' ? "rtl" : "ltr"}
        >
          <option value="" disabled hidden>{placeholderText}</option>
          {elementConfig.options?.map(option => (
            <option key={option.value} value={option.value} dir={currentLang === 'ar' ? "rtl" : "ltr"}>
              {option.displayValue}
            </option>
          ))}
        </select>
      );
      break;
    case "radio":
      inputElement = (
        <div className={classes.radio} dir={currentLang === 'ar' ? "rtl" : "ltr"}>
          {elementConfig.options.map(option => (
            <span key={option.value} className={classes.RadioOption}>
              <input
                type="radio"
                value={option.value}
                onChange={changed}
                name={option.name}
                checked={setChecked(option.value)}
              />{" "}
              <label className={classes.RadioLabel}>{option.label}</label>{" "}
            </span>
          ))}
        </div>
      );
      break;
    default:
      inputElement = (
        <input
          className={inputClasses.join(" ")}
          {...elementConfig}
          value={typeof value !== 'object' ? value : ''}
          {...otherProps}
          onChange={changed}
          onClick={otherProps.clicked}
          placeholder={placeholderText}
          dir={currentLang === 'ar' ? "rtl" : "ltr"}
          {...(isMultiple ? { multiple: true } : {})}
        />
      );
  }
  
  const clearValue = () => {
    if (otherProps.onRemoveValue) otherProps.onRemoveValue();
  };
  
  const tooglePassword = () => {
    setShowPassword(!showPassword);
  };
  
  return (
    <div className={classes.Input} dir={currentLang === 'ar' ? "rtl" : "ltr"}>
      {label ? (
        <label className={classes.Label} dir={currentLang === 'ar' ? "rtl" : "ltr"}>
          {label}
          {shouldValidate && shouldValidate.required ? (
            <span className={classes.ValidationStar}>*</span>
          ) : null}
        </label>
      ) : null}

      <div className={[classes.InputContainer, otherProps.isToolbar && classes.WithToolbar].join(" ")}>
        {/* img with placeholder */}
        {elementConfig && (withIcon ? (<img src={CalendarIcon} alt="Calendar"
          className={classes.Icon} onClick={handleDateInputClick} />) : null)}
        {inputElement}
        {/* customization for some kind of inputs ... */}
        {value &&
          (elementConfig.type == "password" ? (
            <div onClick={tooglePassword} className={classes.showPassword}>
              {showPassword ? (
                <i className="fas fa-eye"></i>
              ) : (
                <i className="fas fa-eye-slash"></i>
              )}
            </div>
          ) : (
            withRemove && (
              <div onClick={clearValue} className={classes.clearInput}>
                <i className="fas fa-times"></i>
              </div>
            )
          ))}
        {otherProps.isToolbar && otherProps.toolbar}
      </div>
      {showCalendar && <Calendar 
        handleDateSelect={handleDateSelect} 
        selectedDate={selectedDates} 
        onClose={() => setShowCalendar(false)} 
        fieldName={label === "Date Of Birth" ? "dateOfBirth" : null}
      />}
      {showValidation ? (
        <p className={classes.ValidationError} dir={currentLang === 'ar' ? "rtl" : "ltr"}>{validationMessage}</p>
      ) : null}
      {otherProps.note ? (
        <p className={classes.Note} dir={currentLang === 'ar' ? "rtl" : "ltr"}>{otherProps.note}</p>
      ) : null}
    </div>
  );
};

export default InputField;
